import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { signOut } from "aws-amplify/auth";
import { Authenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import awsExports from "./aws-exports";
import InviteHandler from "./components/InviteHandler";
import AppStoreRedirect from "./components/AppStoreRedirect";
import Privacy from "./components/Privacy";
import Terms from "./components/Terms";
import Support from "./components/Support";
import NotFound from "./components/NotFound";
import "./App.css";

// Configure Amplify with the same backend as PhomoMobile
Amplify.configure(awsExports);

// Component to handle invite logic
function InvitePage() {
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [showAppStore, setShowAppStore] = useState(false);
  const [clearAuth, setClearAuth] = useState(false);

  useEffect(() => {
    // Clear any existing authentication for testing
    const clearAuthForTesting = async () => {
      try {
        await signOut();
        console.log("🔄 [WEB] Cleared existing auth session for testing");
      } catch (error) {
        // No user signed in, that's fine
        console.log("🔄 [WEB] No existing auth session to clear");
      }
      setClearAuth(true);
    };

    clearAuthForTesting();
  }, []);

  useEffect(() => {
    if (!clearAuth) return; // Wait for auth to be cleared

    // Extract invite token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");

    if (token) {
      setInviteToken(token);
      console.log("🔗 [WEB] Found invite token in URL");
    } else {
      console.log("❌ [WEB] No invite token found - redirecting to app store");
      setShowAppStore(true);
    }
  }, [clearAuth]);

  // Show loading while clearing auth
  if (!clearAuth) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          flexDirection: "column",
          gap: "1rem",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            border: "3px solid #f3f3f3",
            borderTop: "3px solid #667eea",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <p>Preparing invite...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // If no token, show app store redirect
  if (showAppStore) {
    return <AppStoreRedirect />;
  }

  // If we have a token, show the invite handler with auth
  return (
    <div className="App">
      <Authenticator
        socialProviders={[]}
        hideSignUp={false}
        signUpAttributes={["email"]}
        initialState="signUp"
        services={{
          async validateCustomSignUp(formData) {
            const errors: Record<string, string> = {};

            if (!formData.username || formData.username.length < 4) {
              errors.username = "Username must be at least 4 characters long";
            }

            // Birthday validation
            if (!formData.birthdate) {
              errors.birthdate = "Please select your birthday";
            } else {
              // Age requirements by country based on GDPR Article 8
              const ageRequirements: Record<string, number> = {
                // 16 years (GDPR default)
                'AU': 16, 'DE': 16, 'HU': 16, 'LT': 16, 'LU': 16, 'SK': 16, 
                'NL': 16, 'HR': 16, 'RO': 16, 'MT': 16, 'SI': 16,
                // 15 years
                'FR': 15, 'GR': 15, 'PT': 15, 'CZ': 15,
                // 14 years
                'AT': 14, 'BG': 14, 'CY': 14, 'IT': 14, 'EE': 14, 'FI': 14,
                // 13 years (minimum allowed)
                'DK': 13, 'IE': 13, 'LV': 13, 'PL': 13, 'ES': 13, 'SE': 13, 
                'BE': 13, 'GB': 13,
              };

              const getMinAge = () => {
                try {
                  const locale = navigator.language || 'en-US';
                  const countryCode = locale.split('-')[1];
                  return ageRequirements[countryCode || ''] || 13;
                } catch {
                  return 13;
                }
              };

              const birthDate = new Date(formData.birthdate);
              const today = new Date();
              
              // Check if birthdate is in the future
              if (birthDate > today) {
                errors.birthdate = "Birth date cannot be in the future";
              } else {
                let age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                  age--;
                }

                const minAge = getMinAge();
                if (age < minAge) {
                  errors.birthdate = `You must be at least ${minAge} years old to create an account`;
                }
              }
            }

            return Object.keys(errors).length > 0 ? errors : undefined;
          },
        }}
        formFields={{
          signUp: {
            username: {
              order: 1,
              placeholder: "Enter your username (min 4 characters)",
              isRequired: true,
            },
            email: {
              order: 2,
              placeholder: "Enter your email",
              isRequired: true,
            },
            password: {
              order: 3,
              placeholder: "Enter your password",
              isRequired: true,
            },
            confirm_password: {
              order: 4,
              placeholder: "Confirm your password",
              isRequired: true,
            },
            birthdate: {
              order: 5,
              placeholder: "Select your birthday",
              isRequired: true,
              type: "date",
            },
          },
          signIn: {
            username: {
              placeholder: "Enter your username (lowercase only)",
            },
          },
        }}
        components={{
          Header() {
            const urlParams = new URLSearchParams(window.location.search);
            const token = urlParams.get("token");

            return (
              <div className="auth-header">
                <div className="auth-header-content">
                  <img
                    src="/favicon.png"
                    alt="Phomo Logo"
                    className="auth-header-logo"
                  />
                  <h1 className="auth-header-title">
                    PhomoCam
                  </h1>
                </div>
                <p className="auth-header-subtitle">
                  You've been invited to join an event!
                </p>
                <div className="auth-header-links">
                  <a href={`/privacy${token ? `?token=${token}` : ''}`}>
                    Privacy Policy
                  </a>
                  <a href={`/terms${token ? `?token=${token}` : ''}`}>
                    Terms
                  </a>
                  <a href={`/support${token ? `?token=${token}` : ''}`}>
                    Support
                  </a>
                </div>
              </div>
            );
          },
        }}
      >
        {({ user }) => (
          <InviteHandler
            token={inviteToken}
            user={user}
            onError={() => setShowAppStore(true)}
          />
        )}
      </Authenticator>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<InvitePage />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;
